import {
  getDocument,
  updateDocument,
  createBlankDocument,
  generateDocumentWithAI,
} from "~/lib/network/analysis-document";
import { parseUTCDate } from "~/utils/datetime";

export interface AnalysisDocument {
  session_id: string;
  content_html: string;
  created_at: string;
  updated_at: string;
  ai_status: "idle" | "busy";
}

interface AnalysisDocumentState {
  document: AnalysisDocument | null;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  saveError: string | null;
  hasUnsavedChanges: boolean;

  // Actions
  fetchDocument: (sessionId: string) => Promise<void>;
  saveDocument: (sessionId: string, html: string) => Promise<void>;
  createBlankDocument: (sessionId: string) => Promise<void>;
  generateDocumentWithAI: (
    sessionId: string,
    userPrompt: string
  ) => Promise<void>;
  setDocument: (document: AnalysisDocument | null) => void;
  setHasUnsavedChanges: (value: boolean) => void;
  setPanelWidth: (width: number) => void;
  clearError: () => void;
  resetAnalysisDocument: () => void;
}

const defaultStateAttributes = {
  document: null,
  isLoading: false,
  isSaving: false,
  lastSaved: null,
  saveError: null,
  hasUnsavedChanges: false,
  panelWidth: 0,
};

const defaultStateActions = {
  fetchDocument: async () => {},
  saveDocument: async () => {},
  createBlankDocument: async () => {},
  generateDocumentWithAI: async () => {},
  setDocument: () => {},
  setHasUnsavedChanges: () => {},
  setPanelWidth: () => {},
  clearError: () => {},
  resetAnalysisDocument: () => {},
  reset: () => {},
};

const defaultState: AnalysisDocumentState = {
  ...defaultStateAttributes,
  ...defaultStateActions,
};

export const createAnalysisDocumentSlice = (
  set: any,
  get: any,
  _storeApi: any
) => {
  return {
    ...defaultState,

    fetchDocument: async (sessionId: string) => {
      set({ isLoading: true, saveError: null });
      try {
        const document = await getDocument(sessionId);
        set({
          document,
          isLoading: false,
          hasUnsavedChanges: false,
          lastSaved: document ? parseUTCDate(document.updated_at) : null,
        });
        return document;
      } catch (error: any) {
        // 404 is expected for new documents, not an error
        if (error?.response?.status === 404) {
          set({ document: null, isLoading: false });
        } else {
          set({
            isLoading: false,
            saveError: error?.message || "Failed to fetch document",
          });
        }
      }
    },

    saveDocument: async (sessionId: string, html: string) => {
      set({ isSaving: true, saveError: null });
      try {
        const result = await updateDocument(sessionId, html);
        set({
          isSaving: false,
          hasUnsavedChanges: false,
          lastSaved: parseUTCDate(result.updated_at),
          document: {
            ...get().document,
            content_html: html,
            updated_at: result.updated_at,
          },
        });
      } catch (error: any) {
        set({
          isSaving: false,
          saveError: error?.message || "Failed to save document",
        });
        throw error; // Re-throw for handling in component
      }
    },

    createBlankDocument: async (sessionId: string) => {
      set({ isLoading: true, saveError: null });
      try {
        const document = await createBlankDocument(sessionId);
        set({
          document,
          isLoading: false,
          hasUnsavedChanges: false,
          lastSaved: parseUTCDate(document.updated_at),
        });
      } catch (error: any) {
        set({
          isLoading: false,
          saveError: error?.message || "Failed to create document",
        });
        throw error;
      }
    },

    generateDocumentWithAI: async (sessionId: string, userPrompt: string) => {
      set({ isLoading: true, saveError: null });
      try {
        const response = await generateDocumentWithAI(sessionId, userPrompt);
        set({
          document: response,
          isLoading: false,
          hasUnsavedChanges: false,
          lastSaved: parseUTCDate(response.updated_at),
        });
      } catch (error: any) {
        set({
          isLoading: false,
          saveError: error?.message || "Failed to generate document",
        });
        throw error;
      }
    },

    setDocument: (document: AnalysisDocument | null) => {
      set({ document });
    },

    setHasUnsavedChanges: (value: boolean) => {
      set({ hasUnsavedChanges: value });
    },

    clearError: () => {
      set({ saveError: null });
    },

    resetAnalysisDocument: () => set({ ...defaultStateAttributes }),
    reset: () => set({ ...defaultState }),
  };
};
